import React, { useState, useRef, useEffect, JSX } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../context/ThemeContext";

type DropdownOption = {
  label: string;
  checked?: boolean;
  render?: JSX.Element;
};

type ButtonProps = {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  dropdownOptions?: DropdownOption[];
  onOptionChange?: (index: number, checked: boolean) => void;
};

type TitleButtonsProps = {
  title: string;
  buttons?: ButtonProps[];
  marginLeft?: string | number;
  buttonMarginRight?: string | number;
  buttonGap?: number | string;
  outerMarginLeft?: string | number;
  outerMarginRight?: string | number;
};

const TitleButtons: React.FC<TitleButtonsProps> = ({
  title,
  buttons = [],
  marginLeft,
  buttonMarginRight,
  buttonGap = 6,
  outerMarginLeft,
  outerMarginRight,
}) => {
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);

  const [titleWidth, setTitleWidth] = useState(0);
  const [underlineFull, setUnderlineFull] = useState(false);

  const [fontSize, setFontSize] = useState("14px");
  const [titleFontSize, setTitleFontSize] = useState("22px");
  const [buttonHeight, setButtonHeight] = useState(34);

  // 🔹 Lê tema do contexto (ThemeContext)
  const { theme } = useTheme();

  // 🔸 Fallbacks suaves caso o tema ainda não tenha carregado
  const primaryBtnColor = theme?.primary_button || "#124280";
  const secondaryBtnColor = theme?.secondary_button || "#6c757d";
  const lineColorFinal = theme?.line || "#445816";

  // Responsividade + medir largura do título
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setFontSize(Math.max(12, Math.min(16, w / 120)) + "px");
      setTitleFontSize(Math.max(18, Math.min(28, w / 70)) + "px");
      setButtonHeight(w < 1000 ? 32 : w < 1400 ? 34 : 36);
      if (titleRef.current) setTitleWidth(titleRef.current.offsetWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fechar dropdown clicando fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Underline: começa no width do título e cresce até o final (fino)
  useEffect(() => {
    const id = requestAnimationFrame(() => setUnderlineFull(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // converte margens numéricas em px para o CSS
  const parseMargin = (val?: string | number): string =>
    val !== undefined ? (typeof val === "number" ? `${val}px` : val) : "0px";

  const outerRight = parseMargin(outerMarginRight);
  const outerLeft = parseMargin(outerMarginLeft);

  // underline width alvo
  const underlineTargetWidth = `calc(100% - ${outerMarginRight || 0}px`;

  return (
    <div
      style={{
        ...styles.wrapper,
        marginLeft: outerLeft,
        marginRight: outerRight,
      }}
    >
      <div
        style={{
          ...styles.inner,
          width: `calc(100% - ${outerMarginRight || 0}px)`,
          marginLeft: marginLeft ?? 0,
          position: "relative",
        }}
      >
        <div style={{ ...styles.header, position: "relative" }}>
          {/* Título */}
          <span
            ref={titleRef}
            style={{
              ...styles.title,
              fontSize: titleFontSize,
              background: "#fff",
              paddingRight: 10,
              zIndex: 2,
              position: "relative",
              display: "inline-block",
              // 🔹 cor do título = primary_button
              color: primaryBtnColor,
            }}
          >
            {title}
          </span>

          {/* Linha cinza total (base) */}
          <div
            style={{
              position: "absolute",
              bottom: -10,
              left: 0,
              width: `calc(100% - ${outerMarginRight || 0}px)`,
              height: 1,
              backgroundColor: "#E5E5E5",
            }}
          />

          {/* Underline fino animado - usa "line" do tema */}
          <div
            style={{
              position: "absolute",
              bottom: -10,
              left: 0,
              height: 1,
              backgroundColor: lineColorFinal,
              width: underlineFull ? underlineTargetWidth : `${titleWidth}px`,
              transition: "width 520ms ease",
              willChange: "width",
            }}
          />

          {/* Botões */}
          <div style={{ ...styles.buttons, gap: buttonGap }}>
            {buttons.map((btn, idx) => {
              const isSecondary = btn.variant === "secondary";
              return (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    marginRight: buttonMarginRight ?? 0,
                    display: "inline-block",
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      btn.dropdownOptions
                        ? setOpenDropdown(openDropdown === idx ? null : idx)
                        : btn.onClick()
                    }
                    style={{
                      ...styles.button,
                      height: buttonHeight,
                      padding: "0 14px",
                      fontSize,
                      borderRadius: 2,
                      backgroundColor: isSecondary
                        ? secondaryBtnColor
                        : primaryBtnColor,
                      border: "0.5px solid rgba(255,255,255,0.06)",
                      transition:
                        "background-color .15s ease, box-shadow .15s ease, filter .15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = "brightness(0.98)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 6px rgba(18,66,128,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = "none";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.filter = "brightness(0.96)";
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.filter = "brightness(0.98)";
                    }}
                  >
                    {btn.label}
                  </button>

                  {/* Dropdown */}
                  {btn.dropdownOptions && openDropdown === idx && (
                    <div
                      ref={dropdownRef}
                      style={{
                        ...styles.dropdown,
                        top: buttonHeight + 8,
                        width: 260,
                        opacity: 1,
                        transform: "translateY(0)",
                        transition:
                          "opacity 140ms ease, transform 160ms cubic-bezier(.2,.8,.2,1)",
                        border: `1.5px solid ${lineColorFinal}`,
                      }}
                    >
                      <div
                        style={{
                          maxHeight: 300,
                          overflowY: "auto",
                          paddingRight: 2,
                        }}
                      >
                        {btn.dropdownOptions.map((opt, optIdx) => (
                          <label
                            key={optIdx}
                            style={{
                              ...styles.option,
                              fontSize,
                              transition: "background-color 120ms ease",
                              borderRadius: 6,
                              padding: "6px 8px",
                            }}
                            onMouseEnter={(e) => {
                              (
                                e.currentTarget as HTMLLabelElement
                              ).style.backgroundColor = "rgba(18,66,128,0.05)";
                            }}
                            onMouseLeave={(e) => {
                              (
                                e.currentTarget as HTMLLabelElement
                              ).style.backgroundColor = "transparent";
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!opt.checked}
                              onChange={(e) =>
                                btn.onOptionChange?.(optIdx, e.target.checked)
                              }
                              style={{ accentColor: primaryBtnColor }}
                            />
                            <span>{opt.label}</span>
                            {opt.render && (
                              <span style={{ marginLeft: "auto" }}>
                                {opt.render}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setOpenDropdown(null);
                          btn.onClick();
                        }}
                        style={{
                          ...styles.button,
                          ...styles.confirmButton,
                          height: buttonHeight,
                          fontSize,
                          borderRadius: 6,
                          backgroundColor: lineColorFinal,
                          border: "1px solid rgba(18,66,128,0.18)",
                          transition:
                            "background-color .15s ease, box-shadow .15s ease, filter .15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.filter = "brightness(0.985)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 6px rgba(18,66,128,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.filter = "none";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        Gerar PDF
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: { marginTop: 70 },
  inner: { position: "relative" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 30,
  },
  title: {
    color: "#124280", // sobrescrito pelo inline (primaryBtnColor)
    fontFamily: "'Inter', sans-serif",
    fontWeight: 300,
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    userSelect: "none",
    padding: "0 14px",
  },
  dropdown: {
    position: "absolute",
    right: 0,
    background: "#fff",
    borderRadius: 10,
    padding: 10,
    boxShadow:
      "0 8px 18px rgba(10, 31, 68, 0.12), 0 2px 8px rgba(10, 31, 68, 0.06)",
    zIndex: 20,
    opacity: 0,
    transform: "translateY(-6px)",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    color: "#333",
    cursor: "pointer",
  },
  confirmButton: {
    marginTop: 10,
    width: "100%",
  },
};

export default TitleButtons;
